import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { usePlayer } from '../../context/PlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import './PlayerBuyInChart.scss';

const PlayerBuyInChart = () => {
  const { players } = usePlayer();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTotalBuyIn = () => {
    return players.reduce((sum, player) => sum + player.totalBuyIn, 0);
  };

  const getSortedPlayers = () => {
    return [...players].sort((a, b) => b.totalBuyIn - a.totalBuyIn);
  };

  const totalBuyIn = getTotalBuyIn();
  const sortedPlayers = getSortedPlayers();

  if (players.length === 0) {
    return (
      <Card className="player-chart">
        <CardContent>
          <Box className="empty-state">
            <Typography variant="h6" className="empty-title">
              No Players Yet
            </Typography>
            <Typography variant="body2" className="empty-subtitle">
              Add players to see their buy-in chart
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="player-chart">
      <CardContent>
        <Box className="chart-header">
          <Typography variant="h5" className="chart-title">
            Player Buy-ins
          </Typography>
          <Typography variant="body2" color="text.secondary" className="chart-subtitle">
            Total buy-in amounts by player
          </Typography>
        </Box>
        
        <Box className="chart-container">
          <HighchartsReact
            highcharts={Highcharts}
            options={{
              chart: {
                type: 'column',
                backgroundColor: 'transparent',
                height: 300,
                spacing: [20, 20, 20, 20]
              },
              title: {
                text: null
              },
              xAxis: {
                categories: sortedPlayers.map(player => player.name),
                labels: {
                  style: {
                    fontSize: '12px',
                    color: '#757575'
                  },
                  rotation: -45
                },
                gridLineWidth: 0,
                lineWidth: 1,
                lineColor: '#e0e0e0'
              },
              yAxis: {
                title: {
                  text: 'Buy-in Amount ($)',
                  style: {
                    color: '#757575',
                    fontSize: '12px'
                  }
                },
                min: 0,
                max: 5000,
                tickInterval: 1000,
                labels: {
                  style: {
                    fontSize: '11px',
                    color: '#757575'
                  },
                  formatter: function() {
                    return '$' + this.value;
                  }
                },
                gridLineColor: '#e0e0e0',
                gridLineWidth: 1
              },
              tooltip: {
                backgroundColor: '#ffffff',
                borderColor: '#e0e0e0',
                borderRadius: 8,
                shadow: true,
                style: {
                  fontSize: '12px'
                },
                formatter: function() {
                  return `<b>${this.point.name}</b><br/>Buy-in: ${formatCurrency(this.y)}`;
                }
              },
              plotOptions: {
                column: {
                  borderRadius: 4,
                  borderWidth: 0,
                  dataLabels: {
                    enabled: true,
                    format: '${y}',
                    style: {
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: '#757575'
                    },
                    verticalAlign: 'top',
                    y: -5
                  }
                }
              },
              series: [{
                name: 'Buy-in Amount',
                data: sortedPlayers.map(player => ({
                  name: player.name,
                  y: player.totalBuyIn,
                  color: player.profileColor || '#006aff'
                })),
                showInLegend: false
              }],
              credits: {
                enabled: false
              },
              legend: {
                enabled: false
              }
            }}
          />
        </Box>
        
        <Box className="chart-footer">
          <Typography variant="body2" color="text.secondary">
            {sortedPlayers.length} {sortedPlayers.length === 1 ? 'player' : 'players'} • 
            Total: {formatCurrency(totalBuyIn)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PlayerBuyInChart;
